import React, { forwardRef, useImperativeHandle } from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors } from 'react-hook-form';

import Input from 'components/partials/input/input';
import Select from 'components/partials/select/select';
import Textarea from 'components/partials/textarea/textarea';
import RadioInput from 'components/partials/radio-input/radio-input';
import Checkbox from 'components/partials/checkbox/checkbox';

import { formatDate } from 'helpers/dates-formatter';

import {
  ENROLLMENT_TYPES,
  DEFAULT_ENTRY_TERM,
  DATASET_STATUSES,
} from '../../aid-studio-details.constants';

import Icons from 'assets/icons';

interface SourceTableOption {
  value: string;
  label: string;
}

interface DatasetData {
  status?: string;
  datasetTableCreatedDate?: string;
  sourceTableName?: string;
}

interface BasicInfoStepProps {
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  disabled?: boolean;
  sourceTablesOptions: SourceTableOption[];
  sourceTablesLoading: boolean;
  sourceTablesFetching: boolean;
  isDirty: boolean;
  isValid: boolean;
  onSave: () => void;
  isNewModel: boolean;
  recreateDataset: boolean;
  onRecreateDatasetChange: (checked: boolean) => void;
  datasetData?: DatasetData;
}

const BasicInfoStep = forwardRef<unknown, BasicInfoStepProps>(
  (
    {
      control,
      errors,
      disabled,
      sourceTablesOptions,
      sourceTablesLoading,
      sourceTablesFetching,
      isDirty,
      isValid,
      onSave,
      isNewModel,
      recreateDataset,
      onRecreateDatasetChange,
      datasetData,
    },
    ref
  ) => {
    useImperativeHandle(
      ref,
      () => ({
        hasChanges: (isDirty || recreateDataset) && isValid,
        save: onSave,
      }),
      [isDirty, isValid, onSave, recreateDataset]
    );

    const isDatasetInProgress =
      datasetData?.status === DATASET_STATUSES.IN_PROGRESS;

    return (
      <div className="flex gap-8" data-testid="basic-info-step">
        <div className="flex flex-col w-1/2 gap-4">
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Model name is required',
              maxLength: {
                value: 100,
                message: 'Model name cannot exceed 100 characters',
              },
            }}
            render={({ field }) => (
              <Input
                label="Model name"
                labelFor="name"
                labelInTop
                required
                disabled={disabled}
                errorsNames={errors.name as never}
                inputCustomClass="h-10"
                customClass="mt-0 text-md"
                placeholder="Enter model name"
                data-testid="model-name-input"
                {...field}
                value={(field.value as string | undefined) ?? ''}
              />
            )}
          />

          <div className="flex gap-6">
            <div className="w-1/2">
              <Controller
                name="entryTerm"
                control={control}
                rules={{ required: 'Entry term year is required' }}
                render={({ field }) => (
                  <Select
                    label="Entry term year"
                    labelFor="entryTerm"
                    labelInTop
                    required
                    disabled={disabled}
                    fieldError={errors.entryTerm}
                    options={DEFAULT_ENTRY_TERM}
                    customClass="mt-0 text-md"
                    extraBasicStyles={{ height: 40, padding: 0 }}
                    data-testid="entry-term-select"
                    {...field}
                    value={DEFAULT_ENTRY_TERM.find(
                      (opt) => opt.value === field.value
                    )}
                    onChange={(option) => {
                      if (option && !Array.isArray(option)) {
                        field.onChange(option.value);
                      }
                    }}
                  />
                )}
              />
            </div>

            <div className="w-1/2">
              <span className="font-semibold flex mb-2">
                Enrollment type
                <Icons.RequiredIndicator customClass="w-[7px] h-[7px] ml-1 mt-1" />
              </span>
              <Controller
                name="enrollmentType"
                control={control}
                rules={{ required: 'Enrollment type is required' }}
                render={({ field }) => (
                  <div className="flex items-center pt-1 gap-5">
                    {ENROLLMENT_TYPES.map(({ value, label }) => (
                      <RadioInput
                        key={value}
                        label={label}
                        labelFor={`enrollment-${value}`}
                        radioValue={value}
                        disabled={disabled}
                        customClass="m-0"
                        labelClass="whitespace-nowrap text-md"
                        customInputClasses="size-5"
                        data-testid={`enrollment-type-${value}`}
                        {...field}
                        value={(field.value as string | undefined) ?? ''}
                        setAction={field.onChange}
                      />
                    ))}
                  </div>
                )}
              />
              {errors.enrollmentType && (
                <span className="text-sm text-critical-600">
                  {errors.enrollmentType.message}
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Controller
                  name="sourceTableName"
                  control={control}
                  rules={{ required: 'Data science table is required' }}
                  render={({ field }) => (
                    <Select<SourceTableOption>
                      label="Data science table"
                      labelFor="sourceTableName"
                      labelInTop
                      required
                      disabled={
                        disabled ||
                        sourceTablesLoading ||
                        (!isNewModel && !recreateDataset)
                      }
                      fieldError={errors.sourceTableName as never}
                      options={sourceTablesOptions}
                      isLoading={sourceTablesLoading || sourceTablesFetching}
                      placeholder={
                        sourceTablesLoading ? 'Loading...' : 'Select table'
                      }
                      data-testid="source-table-select"
                      customClass="mt-0 text-md"
                      extraBasicStyles={{ height: 40, padding: 0 }}
                      {...field}
                      value={sourceTablesOptions?.find(
                        (opt) => opt.value === field.value
                      )}
                      onChange={(option) => {
                        if (option && !Array.isArray(option)) {
                          field.onChange(option.value);
                        }
                      }}
                    />
                  )}
                />
              </div>

              {!isNewModel && (
                <Checkbox
                  labelFor="recreate-dataset"
                  label="Recreate dataset"
                  checked={recreateDataset}
                  onChange={({ hasAccess: isChecked }) =>
                    onRecreateDatasetChange(isChecked)
                  }
                  disabled={disabled || isDatasetInProgress}
                  customStylesLabel="pb-2 select-none font-semibold text-gray-700"
                  inputCustomClass="size-5"
                  labelTextClass="text-md"
                />
              )}
            </div>

            {!isNewModel && datasetData?.datasetTableCreatedDate && (
              <p
                className="text-xs font-semibold text-gray-700 mt-1"
                data-testid="last-recreated-date"
              >
                Last recreated:{' '}
                {formatDate(
                  datasetData.datasetTableCreatedDate,
                  'MM.DD.YYYY, hh:mmA'
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col w-1/2">
          <Controller
            name="description"
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: 'Description cannot exceed 500 characters',
              },
            }}
            render={({ field }) => (
              <Textarea
                label="Description (optional)"
                labelFor="description"
                customLabelStyles="mb-1"
                textareaStyles="h-[208px]"
                disabled={disabled}
                maxLength={500}
                rows={8}
                showCharCount={false}
                placeholder="Enter model description"
                errorsNames={(errors.description || {}) as never}
                data-testid="model-description-textarea"
                {...field}
                value={(field.value as string | undefined) ?? ''}
              />
            )}
          />
        </div>
      </div>
    );
  }
);

BasicInfoStep.displayName = 'BasicInfoStep';

export default BasicInfoStep;
